<script setup>
// Shared shell for the public content pages (/voor-vve-beheerders,
// /vve-betalingsachterstand). Same chrome as LegalShell — sticky ink-bordered
// header, hero, single reading column — but without the compliance furniture:
// these pages have no version or effective date, and the statutory operator block
// belongs on the legal pages only.
//
// The footer is the real LandingFooter rather than a copy, so the public site has
// exactly one footer to keep correct (its in-page anchors are route links, so they
// work from here too). LegalShell still carries its own; folding those two
// together is a change to compliance pages, which isn't this page's job.
//
// Deliberately plain scrolling: no scroll-snap, no GSAP, no html.snap-pages. These
// pages are read top to bottom — and one of them exists to be genuinely useful to
// someone with an arrears problem — so nothing here may get between the reader and
// the text.
import { useI18n } from "vue-i18n";
import BrandMark from "@/components/BrandMark.vue";
import LandingFooter from "@/components/landing/LandingFooter.vue";
import { buttonVariants } from "@/components/ui/button";
import { scrollToTop } from "@/lib/scroll";

defineProps({
  eyebrow: { type: String, required: true },
  title: { type: String, required: true },
  lead: { type: String, required: true },
});

const { t } = useI18n();
</script>

<template>
  <div class="flex min-h-dvh flex-col">
    <header class="sticky top-0 z-40 border-b-2 border-foreground bg-card">
      <div class="mx-auto flex w-full max-w-6xl items-center gap-4 px-5 py-3">
        <RouterLink to="/" class="inline-flex items-center gap-2.5" aria-label="Pandig" @click="scrollToTop">
          <BrandMark class="size-7 text-primary" />
          <span class="font-sans text-xl font-bold tracking-tight text-foreground">Pandig</span>
        </RouterLink>

        <div class="ml-auto flex items-center gap-3">
          <RouterLink to="/login" class="hidden text-sm font-semibold text-foreground underline-offset-4 hover:underline sm:inline">{{ t("landing.nav.sign_in") }}</RouterLink>
          <RouterLink to="/signup" :class="buttonVariants({ size: 'sm' })">{{ t("landing.nav.sign_up") }}</RouterLink>
        </div>
      </div>
    </header>

    <main class="flex-1">
      <section class="border-b-2 border-foreground bg-card">
        <div class="mx-auto w-full max-w-3xl px-5 py-14 sm:py-20">
          <p class="font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-primary">{{ eyebrow }}</p>
          <h1 class="mt-3 font-sans text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">{{ title }}</h1>
          <p class="mt-5 text-base leading-relaxed text-muted-foreground">{{ lead }}</p>
          <div v-if="$slots.actions" class="mt-7 flex flex-wrap items-center gap-3">
            <slot name="actions" />
          </div>
        </div>
      </section>

      <section class="mx-auto w-full max-w-3xl px-5 py-14 sm:py-20">
        <div class="flex flex-col gap-10">
          <slot />
        </div>
      </section>
    </main>

    <LandingFooter />
  </div>
</template>
